/**
 * Survey Template Editor — rich builder supporting all question types +
 * visibility branching + per-type configuration.
 *
 * Structure:
 *   - Header: metadata + version dropdown + save/publish actions
 *   - Sections: reorderable containers of questions
 *   - Question row: inline row with a per-type config panel that expands
 *   - Branching editor: add show/hide rules referencing prior questions
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog as DialogRaw, DialogContent as DialogContentRaw,
  DialogHeader as DialogHeaderRaw, DialogTitle as DialogTitleRaw,
  DialogDescription as DialogDescriptionRaw, DialogFooter as DialogFooterRaw,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BranchingEditor } from '@/pages/insurer/BranchingEditor';
import {
  Plus, Trash2, GitBranch, Save, Send, ChevronDown, ChevronRight,
  Copy, Settings2, ShieldCheck, Settings, MinusCircle, Printer, GripVertical, PenLine, MapPin,
} from 'lucide-react';

// Cast shadcn dialog exports (.jsx) for TS strict mode
type DlgProps = { className?: string; children?: React.ReactNode; [k: string]: unknown };
const Dialog = DialogRaw as unknown as React.ComponentType<DlgProps>;
const DialogContent = DialogContentRaw as unknown as React.ComponentType<DlgProps>;
const DialogHeader = DialogHeaderRaw as unknown as React.ComponentType<DlgProps>;
const DialogTitle = DialogTitleRaw as unknown as React.ComponentType<DlgProps>;
const DialogDescription = DialogDescriptionRaw as unknown as React.ComponentType<DlgProps>;
const DialogFooter = DialogFooterRaw as unknown as React.ComponentType<DlgProps>;

// ---------- Types ----------
interface VisibilityCondition {
  question_code: string;
  op: string;
  value: unknown;
}
type VNode =
  | { kind: 'rule'; question_code: string; op: string; value?: unknown }
  | { kind: 'group'; logic: 'all' | 'any'; children: VNode[] };
interface Question {
  question_code: string;
  display_number: string;
  text: string;
  type: string;
  required?: boolean;
  help_text?: string;
  options?: string[];
  default_value?: unknown;
  condition?: VisibilityCondition | null;
  conditions?: VisibilityCondition[];
  visibility_logic?: 'all' | 'any';
  visibility_tree?: VNode | null;
  risk_weight?: number;
  config?: Record<string, unknown>;
}
interface Section {
  section_code: string;
  number: string;
  title: string;
  description?: string;
  questions: Question[];
}

// Re-sequence a section's questions so display_number stays numerical and in
// order (e.g. after a move/reorder "1.2" above "1.1" becomes "1.1"/"1.2").
function renumberQuestions(questions: Question[], sectionNumber: string): Question[] {
  return questions.map((q, i) => ({ ...q, display_number: `${sectionNumber}.${i + 1}` }));
}

// Re-sequence sections 1..N and cascade each section's number onto its
// questions' display numbers (so a reordered section 3 → 2 makes its
// questions 2.1, 2.2, …).
function renumberSections(sections: Section[]): Section[] {
  return sections.map((s, i) => {
    const num = String(i + 1);
    return { ...s, number: num, questions: renumberQuestions(s.questions, num) };
  });
}

interface Schema {
  sections: Section[];
  signature_slots?: Array<{ role: string; label: string; require_name?: boolean; required?: boolean; selfie_mode?: 'off' | 'optional' | 'required' }>;
  signatures_configured?: boolean;
  photo_gallery_enabled?: boolean;
  photo_min_count?: number;
  geofence_required?: boolean;
  geo_mode?: 'strict' | 'capture_only';
  geo_threshold_m?: number;
  geo_hard_cap_m?: number;
}
interface Version {
  id: string; version: number; status: string; changelog: string;
  created_at: string; schema: Schema;
}
interface Template {
  id: string; name: string; description: string;
  current_published_version?: number; latest_version?: number;
  versions_count: number; versions: Version[];
  active?: boolean; scoring_enabled?: boolean; scoring_locked?: boolean;
}
interface QTypeMeta {
  id: string; label: string; group: string;
  needs_options: boolean; is_scored_by_default: boolean;
  answer_shape: string;
  scale?: { min: number; max: number; step?: number };
  default_options?: string[];
}

// -------------------------------------------------------------- Root page
export const TemplateEditorPage: React.FC = () => {
  const { templateId } = useParams();
  const [tpl, setTpl] = useState<Template | null>(null);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [schema, setSchema] = useState<Schema>({ sections: [] });
  const [busy, setBusy] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [types, setTypes] = useState<QTypeMeta[]>([]);
  const [branchOps, setBranchOps] = useState<string[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Drag-and-drop reordering within a section (HTML5 native DnD).
  const dragRef = React.useRef<{ sIdx: number; qIdx: number } | null>(null);
  const sectionDragRef = React.useRef<number | null>(null);

  useEffect(() => {
    api.get<{ types: QTypeMeta[]; branch_ops: string[] }>('/templates/meta/question-types')
      .then((r) => { setTypes(r.data.types); setBranchOps(r.data.branch_ops); })
      .catch(() => { /* keep defaults */ });
  }, []);

  const load = async (): Promise<void> => {
    if (!templateId) return;
    try {
      const r = await api.get<Template>(`/templates/${templateId}`);
      setTpl(r.data);
      const active = r.data.versions[0];
      if (active) {
        setActiveVersion(active.version); setSchema(active.schema);
        // Default: expand the first section so the builder is immediately usable.
        const firstSection = (active.schema?.sections?.[0]?.section_code) || null;
        setExpandedSection(firstSection);
      }
    } catch {
      toast.error('Failed to load template');
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [templateId]);

  // ---- Prior questions available for branching (any question above the current one) ----
  const priorQuestions = useMemo(() => {
    const flat: Question[] = [];
    schema.sections.forEach((s) => { s.questions.forEach((q) => flat.push(q)); });
    return flat;
  }, [schema]);

  // ---- Scoring flag preview ----
  const isScored = useMemo(() => {
    for (const s of schema.sections) {
      for (const q of s.questions) {
        if ((q.risk_weight ?? 0) > 0 && ['yes_no', 'yes_no_na'].includes(q.type)) return true;
      }
    }
    return false;
  }, [schema]);

  const expandAllQuestions = (): void => {
    const s = new Set<string>();
    schema.sections.forEach((sec) => sec.questions.forEach((q) => s.add(q.question_code)));
    setExpandedQuestions(s);
  };
  const collapseAllQuestions = (): void => setExpandedQuestions(new Set());

  if (!tpl) return <div className="text-raptor-navy/60 font-body py-8 text-center">Loading…</div>;
  const activeVer = tpl.versions.find((v) => v.version === activeVersion);
  const isDraft = activeVer?.status === 'draft';

  // ---- Save/Publish/Version ----
  const saveDraft = async (): Promise<void> => {
    if (!activeVer || !isDraft) return;
    setBusy(true);
    try {
      await api.put(`/templates/${templateId}/versions/${activeVersion}/schema`, schema);
      toast.success('Draft saved');
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string; error?: string } } };
      toast.error(e?.response?.data?.detail ?? e?.response?.data?.error ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  // Persist the per-template geo policy (lives inside the version schema) from
  // the Template settings dialog. Only writable on a draft version.
  const persistGeo = async (mode: 'strict' | 'capture_only', thresholdM: number): Promise<void> => {
    const nextSchema = { ...schema, geo_mode: mode, geo_threshold_m: thresholdM, geofence_required: mode === 'strict' };
    setSchema(nextSchema);
    if (isDraft && activeVer) {
      await api.put(`/templates/${templateId}/versions/${activeVersion}/schema`, nextSchema);
    }
  };
  const publish = async (): Promise<void> => {
    if (!activeVer || !isDraft) return;
    if (!confirm(`Publish version ${activeVer.version}? Once published this version becomes immutable.`)) return;
    await api.post(`/templates/${templateId}/versions/${activeVersion}/publish`);
    toast.success(`v${activeVer.version} published`);
    await load();
  };
  const newVersion = async (): Promise<void> => {
    const changelog = prompt('Changelog for the new draft version:');
    if (changelog === null) return;
    const r = await api.post<{ version: number }>(`/templates/${templateId}/versions`, { schema, changelog });
    toast.success(`Draft v${r.data.version} created`);
    await load();
    setActiveVersion(r.data.version);
  };

  // ---- Section ops ----
  // ---- Review & Sign signature blocks (schema.signature_slots) ----
  const DEFAULT_SIG_SLOTS: NonNullable<Schema['signature_slots']> = [
    { role: 'technician', label: 'Technician / Surveyor', require_name: true, required: false },
    { role: 'client', label: 'Client', require_name: true, required: false },
  ];
  const sigConfigured = !!schema.signatures_configured;
  const effectiveSlots: NonNullable<Schema['signature_slots']> = sigConfigured ? (schema.signature_slots ?? []) : DEFAULT_SIG_SLOTS;
  const commitSlots = (next: NonNullable<Schema['signature_slots']>): void =>
    setSchema({ ...schema, signature_slots: next, signatures_configured: true });
  const addSigSlot = (): void => commitSlots([...effectiveSlots, { role: `signer_${Date.now()}`, label: 'New signer', require_name: true, required: false }]);
  const patchSigSlot = (i: number, patch: Partial<NonNullable<Schema['signature_slots']>[number]>): void =>
    commitSlots(effectiveSlots.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const removeSigSlot = (i: number): void => commitSlots(effectiveSlots.filter((_, j) => j !== i));
  const moveSigSlot = (i: number, dir: number): void => {
    const n = [...effectiveSlots]; const j = i + dir;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]]; commitSlots(n);
  };

  const addSection = (): void => {
    const n = String(schema.sections.length + 1);
    setSchema({
      ...schema,
      sections: [...schema.sections, { section_code: `SEC${Date.now()}`, number: n, title: 'New section', description: '', questions: [] }],
    });
  };
  const updateSection = (idx: number, patch: Partial<Section>): void => {
    setSchema({ ...schema, sections: schema.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  };
  const removeSection = (idx: number): void => {
    if (!confirm('Delete this section and all its questions?')) return;
    setSchema({ ...schema, sections: renumberSections(schema.sections.filter((_, i) => i !== idx)) });
  };
  const moveSection = (idx: number, delta: number): void => {
    const next = [...schema.sections];
    const tgt = idx + delta;
    if (tgt < 0 || tgt >= next.length) return;
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    setSchema({ ...schema, sections: renumberSections(next) });
  };
  const reorderSection = (from: number, to: number): void => {
    if (from === to) return;
    const next = [...schema.sections];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setSchema({ ...schema, sections: renumberSections(next) });
  };

  // ---- Question ops ----
  const addQuestion = (sIdx: number): void => {
    const s = schema.sections[sIdx];
    const q: Question = {
      question_code: `Q_${Date.now()}`,
      display_number: `${s.number}.${s.questions.length + 1}`,
      text: 'New question',
      type: 'yes_no',
      required: false,
      options: [],
      conditions: [],
      risk_weight: 0,
      config: {},
    };
    setSchema({
      ...schema,
      sections: schema.sections.map((sec, i) => (i === sIdx ? { ...sec, questions: [...sec.questions, q] } : sec)),
    });
    setExpandedQuestions((prev) => new Set(prev).add(q.question_code));
  };
  const updateQuestion = (sIdx: number, qIdx: number, patch: Partial<Question>): void => {
    setSchema({
      ...schema,
      sections: schema.sections.map((sec, i) => i === sIdx ? {
        ...sec, questions: sec.questions.map((q, j) => j === qIdx ? { ...q, ...patch } : q),
      } : sec),
    });
  };
  const removeQuestion = (sIdx: number, qIdx: number): void => {
    if (!confirm('Delete this question?')) return;
    setSchema({
      ...schema,
      sections: schema.sections.map((sec, i) => i === sIdx ? {
        ...sec, questions: renumberQuestions(sec.questions.filter((_, j) => j !== qIdx), sec.number),
      } : sec),
    });
  };
  const moveQuestion = (sIdx: number, qIdx: number, delta: number): void => {
    const sec = schema.sections[sIdx];
    const next = [...sec.questions];
    const tgt = qIdx + delta;
    if (tgt < 0 || tgt >= next.length) return;
    [next[qIdx], next[tgt]] = [next[tgt], next[qIdx]];
    updateSection(sIdx, { questions: renumberQuestions(next, sec.number) });
  };
  const reorderQuestion = (sIdx: number, from: number, to: number): void => {
    if (from === to) return;
    const sec = schema.sections[sIdx];
    const next = [...sec.questions];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    updateSection(sIdx, { questions: renumberQuestions(next, sec.number) });
  };
  const duplicateQuestion = (sIdx: number, qIdx: number): void => {
    const src = schema.sections[sIdx].questions[qIdx];
    const copy: Question = { ...src, question_code: `Q_${Date.now()}`, text: `${src.text} (copy)` };
    setSchema({
      ...schema,
      sections: schema.sections.map((sec, i) => i === sIdx ? {
        ...sec, questions: renumberQuestions([...sec.questions.slice(0, qIdx + 1), copy, ...sec.questions.slice(qIdx + 1)], sec.number),
      } : sec),
    });
  };

  const printTemplate = async (): Promise<void> => {
    if (!templateId || activeVersion == null) return;
    try {
      const r = await api.get(`/templates/${templateId}/versions/${activeVersion}/print`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data as Blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error('Could not generate the PDF.');
    }
  };

  // ---- Prior questions + scoring flag are memoised above (before early return). ----

  return (
    <div className="space-y-4 animate-fade-in" data-testid="template-editor-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">{tpl.name}</h2>
            {tpl.scoring_enabled ? (
              <Badge variant="blue" className="gap-1"><ShieldCheck size={12} /> Score rated</Badge>
            ) : (
              <Badge variant="muted" className="gap-1"><MinusCircle size={12} /> Report only</Badge>
            )}
            {tpl.active === false && <Badge variant="muted">Inactive</Badge>}
          </div>
          <p className="text-sm text-raptor-navy/60 font-body">{tpl.description}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} data-testid="template-settings-btn">
            <Settings size={14} className="mr-1" /> Template settings
          </Button>
          <Button size="sm" variant="outline" onClick={() => void newVersion()} data-testid="new-version-btn">
            <GitBranch size={14} className="mr-1" /> New draft
          </Button>
          <Button size="sm" variant="outline" onClick={() => void printTemplate()} data-testid="print-template-btn">
            <Printer size={14} className="mr-1" /> Print PDF
          </Button>
          {isDraft && (
            <Button size="sm" variant="gradient" onClick={() => void saveDraft()} disabled={busy} data-testid="save-draft-btn">
              <Save size={14} className="mr-1" /> Save draft
            </Button>
          )}
          {isDraft && (
            <Button size="sm" variant="primary" onClick={() => void publish()} data-testid="publish-btn">
              <Send size={14} className="mr-1" /> Publish v{activeVersion}
            </Button>
          )}
        </div>
      </div>

      {settingsOpen && (
        <TemplateSettingsDialog
          tpl={tpl}
          computedScored={isScored}
          isDraft={!!isDraft}
          geoMode={schema.geo_mode ?? 'strict'}
          geoThresholdM={schema.geo_threshold_m ?? 200}
          onSaveGeo={persistGeo}
          onClose={() => setSettingsOpen(false)}
          onSaved={async () => { setSettingsOpen(false); await load(); }}
        />
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="tpl-version-select" className="text-sm font-body text-raptor-navy/70">Working version</label>
        <select
          id="tpl-version-select"
          data-testid="template-version-select"
          className="rounded-md border border-border/60 bg-white px-3 py-1.5 text-sm font-tabular text-raptor-navy focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
          value={activeVersion ?? ''}
          onChange={(e) => {
            const target = tpl.versions.find((v) => v.version === Number(e.target.value));
            if (target) { setActiveVersion(target.version); setSchema(target.schema); }
          }}
        >
          {tpl.versions.map((v) => (
            <option key={v.id} value={v.version}>
              v{v.version} — {v.status === 'published' ? 'published' : 'draft'}
              {v.version === tpl.current_published_version ? ' (current)' : ''}
            </option>
          ))}
        </select>
        {activeVer && (
          <Badge variant={activeVer.status === 'published' ? 'success' : 'muted'}>
            {activeVer.status === 'published' ? 'Published' : 'Draft'}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={expandAllQuestions} data-testid="expand-all-btn">
            Expand all questions
          </Button>
          <Button size="sm" variant="outline" onClick={collapseAllQuestions} data-testid="collapse-all-btn">
            Collapse all
          </Button>
        </div>
      </div>

      {activeVer && (
        <div className="space-y-3">
          {schema.sections.map((sec, sIdx) => (
            <Card
              key={sec.section_code}
              onDragOver={(e) => { if (sectionDragRef.current != null) e.preventDefault(); }}
              onDrop={(e) => {
                if (sectionDragRef.current != null) {
                  e.preventDefault();
                  reorderSection(sectionDragRef.current, sIdx);
                  sectionDragRef.current = null;
                }
              }}
              data-testid={`section-card-${sIdx}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {isDraft && (
                    <span
                      draggable
                      onDragStart={() => { sectionDragRef.current = sIdx; }}
                      onDragEnd={() => { sectionDragRef.current = null; }}
                      className="cursor-grab text-raptor-navy/30 hover:text-raptor-navy/60"
                      title="Drag to reorder section"
                      data-testid={`section-drag-${sIdx}`}
                    >
                      <GripVertical size={16} />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setExpandedSection(expandedSection === sec.section_code ? null : sec.section_code)}
                    data-testid={`section-toggle-${sIdx}`}
                    className="p-1 hover:bg-raptor-neutral/40 rounded"
                  >
                    {expandedSection === sec.section_code ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  <Input className="w-16" value={sec.number} onChange={(e) => updateSection(sIdx, { number: e.target.value })} disabled={!isDraft} />
                  <Input
                    value={sec.title}
                    onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                    disabled={!isDraft}
                    className="flex-1 min-w-[240px] font-semibold"
                    data-testid={`section-title-${sIdx}`}
                  />
                  <Badge variant="muted">{sec.questions.length} q</Badge>
                  {isDraft && (
                    <>
                      <Button size="icon" variant="ghost" onClick={() => moveSection(sIdx, -1)} title="Move up">↑</Button>
                      <Button size="icon" variant="ghost" onClick={() => moveSection(sIdx, +1)} title="Move down">↓</Button>
                      <Button size="icon" variant="ghost" onClick={() => removeSection(sIdx)} data-testid={`section-delete-${sIdx}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
                {expandedSection === sec.section_code && (
                  <div className="mt-3 space-y-2 pl-6">
                    <div className="grid gap-2">
                      <Label className="text-xs">Section description (shown to surveyors)</Label>
                      <Textarea
                        value={sec.description || ''}
                        onChange={(e) => updateSection(sIdx, { description: e.target.value })}
                        rows={2}
                        disabled={!isDraft}
                      />
                    </div>
                    {sec.questions.map((q, qIdx) => (
                      <div
                        key={q.question_code + qIdx}
                        draggable={!!isDraft}
                        onDragStart={() => { dragRef.current = { sIdx, qIdx }; }}
                        onDragOver={(e) => { if (dragRef.current?.sIdx === sIdx) e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = dragRef.current;
                          if (from && from.sIdx === sIdx && from.qIdx !== qIdx) reorderQuestion(sIdx, from.qIdx, qIdx);
                          dragRef.current = null;
                        }}
                        data-testid={`question-drag-${sIdx}-${qIdx}`}
                      >
                      <QuestionRow
                        q={q}
                        types={types}
                        branchOps={branchOps}
                        priorQuestions={priorQuestions}
                        isDraft={!!isDraft}
                        isExpanded={expandedQuestions.has(q.question_code)}
                        onExpand={() => {
                          setExpandedQuestions((prev) => {
                            const next = new Set(prev);
                            if (next.has(q.question_code)) next.delete(q.question_code);
                            else next.add(q.question_code);
                            return next;
                          });
                        }}
                        onChange={(patch) => updateQuestion(sIdx, qIdx, patch)}
                        onRemove={() => removeQuestion(sIdx, qIdx)}
                        onDuplicate={() => duplicateQuestion(sIdx, qIdx)}
                        onMoveUp={() => moveQuestion(sIdx, qIdx, -1)}
                        onMoveDown={() => moveQuestion(sIdx, qIdx, +1)}
                        testIdx={`${sIdx}-${qIdx}`}
                      />
                      </div>
                    ))}
                    {isDraft && (
                      <Button size="sm" variant="outline" onClick={() => addQuestion(sIdx)} data-testid={`add-question-${sIdx}`}>
                        <Plus size={14} className="mr-1" /> Add question
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {isDraft && (
            <Button variant="outline" onClick={addSection} data-testid="add-section-btn">
              <Plus size={14} className="mr-1" /> Add section
            </Button>
          )}

          {/* Review & Sign — editable signature blocks shown at the end of every survey */}
          <Card className="border-raptor-blue/40" data-testid="review-sign-card">
            <CardContent className="p-4 space-y-3">
              <div>
                <div className="flex items-center gap-2 text-raptor-navy font-headline text-lg"><PenLine size={18} /> Review &amp; Sign</div>
                <div className="text-sm text-raptor-navy/60 font-body mt-1">
                  Signature blocks shown on the final step of every survey built from this template. Add, rename, reorder or remove them.
                  {!sigConfigured && ' Currently using the default Technician + Client blocks — edit below to customise.'}
                </div>
              </div>
              {effectiveSlots.length === 0 && (
                <div className="text-sm text-raptor-navy/60 italic">No signature blocks — surveys will end with the review summary only (no signing).</div>
              )}
              {effectiveSlots.map((slot, i) => (
                <div key={i} className="rounded-md border border-border/60 p-3 space-y-2" data-testid={`sig-slot-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input
                      value={slot.label}
                      disabled={!isDraft}
                      placeholder="Signature label (e.g. Client, Store Manager)"
                      onChange={(e) => patchSigSlot(i, { label: e.target.value })}
                      data-testid={`sig-slot-label-${i}`}
                    />
                    {isDraft && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => moveSigSlot(i, -1)} data-testid={`sig-slot-up-${i}`}><ChevronDown size={14} className="rotate-180" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => moveSigSlot(i, 1)} data-testid={`sig-slot-down-${i}`}><ChevronDown size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => removeSigSlot(i)} data-testid={`sig-slot-remove-${i}`}><Trash2 size={14} className="text-red-500" /></Button>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-raptor-navy/70">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" disabled={!isDraft} checked={slot.require_name !== false} onChange={(e) => patchSigSlot(i, { require_name: e.target.checked })} data-testid={`sig-slot-name-${i}`} />
                      Collect signer name
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" disabled={!isDraft} checked={!!slot.required} onChange={(e) => patchSigSlot(i, { required: e.target.checked })} data-testid={`sig-slot-required-${i}`} />
                      Required to submit
                    </label>
                    <label className="flex items-center gap-1.5">
                      <span>Selfie</span>
                      <select
                        disabled={!isDraft}
                        value={slot.selfie_mode ?? 'off'}
                        onChange={(e) => patchSigSlot(i, { selfie_mode: e.target.value as 'off' | 'optional' | 'required' })}
                        className="border border-border/60 rounded px-1.5 py-0.5 bg-white text-xs"
                        data-testid={`sig-slot-selfie-${i}`}
                      >
                        <option value="off">Off</option>
                        <option value="optional">Optional</option>
                        <option value="required">Required</option>
                      </select>
                    </label>
                  </div>
                </div>
              ))}
              {isDraft && (
                <Button variant="outline" onClick={addSigSlot} data-testid="add-sig-slot-btn">
                  <Plus size={14} className="mr-1" /> Add signature block
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};


// -------------------------------------------------------------- Question row
const QuestionRow: React.FC<{
  q: Question;
  types: QTypeMeta[];
  branchOps: string[];
  priorQuestions: Question[];
  isDraft: boolean;
  isExpanded: boolean;
  onExpand: () => void;
  onChange: (patch: Partial<Question>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  testIdx: string;
}> = ({ q, types, branchOps, priorQuestions, isDraft, isExpanded, onExpand, onChange, onRemove, onDuplicate, onMoveUp, onMoveDown, testIdx }) => {
  const meta = types.find((t) => t.id === q.type);
  const needsOptions = meta?.needs_options ?? false;
  const isScoredType = ['yes_no', 'yes_no_na'].includes(q.type);
  const ruleCount = (q.conditions?.length ?? 0) + (q.condition ? 1 : 0);

  return (
    <div className="bg-raptor-neutral/20 rounded-md border border-border/40" data-testid={`question-${testIdx}`}>
      <div className="grid grid-cols-[auto_1fr_auto] gap-2 p-2 items-center">
        <button
          type="button"
          className="p-1 hover:bg-white/60 rounded"
          onClick={onExpand}
          data-testid={`question-toggle-${testIdx}`}
          title={isExpanded ? 'Collapse question' : 'Expand to edit options, config and branching rules'}
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="grid grid-cols-[80px_1fr_180px] gap-2 items-center">
          <Input value={q.display_number} onChange={(e) => onChange({ display_number: e.target.value })} disabled={!isDraft} placeholder="#" className="text-xs font-tabular" />
          <Input value={q.text} onChange={(e) => onChange({ text: e.target.value })} disabled={!isDraft} className="font-body" data-testid={`question-text-${testIdx}`} />
          <select
            className="rounded-md border border-border/60 bg-white px-2 py-2 text-sm"
            value={q.type}
            onChange={(e) => onChange({ type: e.target.value })}
            disabled={!isDraft}
            data-testid={`question-type-${testIdx}`}
          >
            {['boolean', 'text', 'choice', 'scale', 'matrix', 'numeric', 'datetime', 'location', 'media', 'display'].map((grp) => {
              const inGroup = types.filter((t) => t.group === grp);
              if (!inGroup.length) return null;
              return (
                <optgroup key={grp} label={grp}>
                  {inGroup.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </optgroup>
              );
            })}
          </select>
        </div>
        <div className="flex items-center gap-1">
          {ruleCount > 0 && (
            <Badge variant="blue" className="gap-1 text-[10px]" data-testid={`branch-badge-${testIdx}`}>
              <GitBranch size={10} /> {ruleCount} rule{ruleCount === 1 ? '' : 's'}
            </Badge>
          )}
          <label className="text-xs flex items-center gap-1 mr-2">
            <input type="checkbox" checked={q.required ?? false} onChange={(e) => onChange({ required: e.target.checked })} disabled={!isDraft} data-testid={`question-req-${testIdx}`} />
            req
          </label>
          {isDraft && (
            <>
              <Button size="icon" variant="ghost" onClick={onMoveUp} title="Move up">↑</Button>
              <Button size="icon" variant="ghost" onClick={onMoveDown} title="Move down">↓</Button>
              <Button size="icon" variant="ghost" onClick={onDuplicate} title="Duplicate"><Copy size={12} /></Button>
              <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`question-delete-${testIdx}`}><Trash2 size={12} className="text-red-500" /></Button>
            </>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-border/40 p-3 space-y-3 bg-white/60">
          <div className="grid gap-2">
            <Label className="text-xs">Question code (immutable across versions)</Label>
            <Input value={q.question_code} disabled className="font-tabular text-xs" />
          </div>
          <div className="grid gap-2">
            <Label className="text-xs">Help text (shown under the question)</Label>
            <Input value={q.help_text || ''} onChange={(e) => onChange({ help_text: e.target.value })} disabled={!isDraft} />
          </div>

          {/* Options editor for choice-based types */}
          {needsOptions && (
            <OptionsEditor
              options={q.options || meta?.default_options || []}
              onChange={(opts) => onChange({ options: opts })}
              disabled={!isDraft}
              testIdx={testIdx}
            />
          )}

          {/* Per-type config */}
          <TypeConfigPanel q={q} meta={meta} onChange={(cfg) => onChange({ config: cfg })} disabled={!isDraft} testIdx={testIdx} />

          {/* Risk weight (only meaningful for scored yes/no types) */}
          <div className="grid grid-cols-[200px_1fr] gap-2 items-center">
            <Label className="text-xs">
              Risk weight
              {!isScoredType && <span className="ml-1 text-raptor-navy/50">(scored types only)</span>}
            </Label>
            <Input
              type="number" step={0.5} min={0}
              value={q.risk_weight ?? 0}
              onChange={(e) => onChange({ risk_weight: Number(e.target.value) || 0 })}
              disabled={!isDraft || !isScoredType}
              className="font-tabular"
              placeholder="0"
              data-testid={`question-risk-weight-${testIdx}`}
            />
          </div>

          {/* Branching */}
          <BranchingEditor
            q={q}
            priorQuestions={priorQuestions}
            branchOps={branchOps}
            onChange={(patch) => onChange(patch)}
            disabled={!isDraft}
            testIdx={testIdx}
          />
        </div>
      )}
    </div>
  );
};


// -------------------------------------------------------------- Options editor
const OptionsEditor: React.FC<{
  options: string[];
  onChange: (options: string[]) => void;
  disabled?: boolean;
  testIdx: string;
}> = ({ options, onChange, disabled, testIdx }) => {
  return (
    <div className="grid gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
      <Label className="text-xs flex items-center gap-1"><Settings2 size={12} /> Options</Label>
      <div className="space-y-1">
        {options.map((opt, i) => (
          <div key={`opt-${testIdx}-${i}`} className="flex items-center gap-1">
            <Input
              value={opt}
              onChange={(e) => {
                const next = [...options]; next[i] = e.target.value; onChange(next);
              }}
              disabled={disabled}
              className="flex-1"
              data-testid={`option-${testIdx}-${i}`}
            />
            {!disabled && (
              <Button size="icon" variant="ghost" onClick={() => onChange(options.filter((_, j) => j !== i))}>
                <Trash2 size={12} className="text-red-500" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <Button size="sm" variant="outline" onClick={() => onChange([...options, `Option ${options.length + 1}`])} data-testid={`add-option-${testIdx}`}>
          <Plus size={12} className="mr-1" /> Add option
        </Button>
      )}
    </div>
  );
};


// -------------------------------------------------------------- Type config
const TypeConfigPanel: React.FC<{
  q: Question;
  meta: QTypeMeta | undefined;
  onChange: (config: Record<string, unknown>) => void;
  disabled?: boolean;
  testIdx: string;
}> = ({ q, meta, onChange, disabled, testIdx }) => {
  const cfg = q.config || {};
  const update = (patch: Record<string, unknown>): void => onChange({ ...cfg, ...patch });

  if (!meta) return null;
  if (meta.answer_shape === 'none') return null;

  // Scale-based inputs
  if (['nps', 'rating', 'slider'].includes(q.type)) {
    const defMin = meta.scale?.min ?? 0;
    const defMax = meta.scale?.max ?? 10;
    const defStep = meta.scale?.step ?? 1;
    return (
      <div className="grid grid-cols-3 gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid gap-1">
          <Label className="text-xs">Scale min</Label>
          <Input type="number" value={(cfg.scale_min as number) ?? defMin} onChange={(e) => update({ scale_min: Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-min-${testIdx}`} />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Scale max</Label>
          <Input type="number" value={(cfg.scale_max as number) ?? defMax} onChange={(e) => update({ scale_max: Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-max-${testIdx}`} />
        </div>
        {q.type === 'slider' && (
          <div className="grid gap-1">
            <Label className="text-xs">Step</Label>
            <Input type="number" value={(cfg.scale_step as number) ?? defStep} onChange={(e) => update({ scale_step: Number(e.target.value) })} disabled={disabled} className="font-tabular" />
          </div>
        )}
      </div>
    );
  }

  // Matrix
  if (q.type === 'matrix') {
    const rows: string[] = (cfg.rows as string[]) || [];
    const cols: string[] = (cfg.columns as string[]) || [];
    const setRows = (r: string[]): void => update({ rows: r });
    const setCols = (c: string[]): void => update({ columns: c });
    return (
      <div className="grid gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <Label className="text-xs">Matrix configuration</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-raptor-navy/60 mb-1">Rows (subjects)</div>
            {rows.map((r, i) => (
              <div key={`row-${i}`} className="flex items-center gap-1 mb-1">
                <Input value={r} onChange={(e) => { const n = [...rows]; n[i] = e.target.value; setRows(n); }} disabled={disabled} />
                {!disabled && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={12} className="text-red-500" /></Button>}
              </div>
            ))}
            {!disabled && <Button size="sm" variant="outline" onClick={() => setRows([...rows, `Row ${rows.length + 1}`])}><Plus size={12} className="mr-1" /> Add row</Button>}
          </div>
          <div>
            <div className="text-xs text-raptor-navy/60 mb-1">Columns (choices)</div>
            {cols.map((c, i) => (
              <div key={`col-${i}`} className="flex items-center gap-1 mb-1">
                <Input value={c} onChange={(e) => { const n = [...cols]; n[i] = e.target.value; setCols(n); }} disabled={disabled} />
                {!disabled && <Button size="icon" variant="ghost" onClick={() => setCols(cols.filter((_, j) => j !== i))}><Trash2 size={12} className="text-red-500" /></Button>}
              </div>
            ))}
            {!disabled && <Button size="sm" variant="outline" onClick={() => setCols([...cols, `Col ${cols.length + 1}`])}><Plus size={12} className="mr-1" /> Add column</Button>}
          </div>
        </div>
      </div>
    );
  }

  // Text-like with min/max length
  if (['short_text', 'long_text', 'email', 'phone'].includes(q.type)) {
    const defMax = q.type === 'short_text' ? 255 : q.type === 'long_text' ? 1500 : undefined;
    const effMax = (cfg.max_length as number) ?? defMax;
    return (
      <div className="grid gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid grid-cols-3 gap-2">
          <div className="grid gap-1">
            <Label className="text-xs">Placeholder</Label>
            <Input value={(cfg.placeholder as string) || ''} onChange={(e) => update({ placeholder: e.target.value })} disabled={disabled} />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Min length</Label>
            <Input type="number" value={(cfg.min_length as number) ?? ''} onChange={(e) => update({ min_length: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Max length</Label>
            <Input type="number" value={(cfg.max_length as number) ?? (defMax ?? '')} onChange={(e) => update({ max_length: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-maxlen-${testIdx}`} />
          </div>
        </div>
        {(q.type === 'short_text' || q.type === 'long_text') && effMax != null && (
          <div className="text-[11px] text-raptor-navy/60">{q.type === 'short_text' ? 'Short text' : 'Long text'} — up to <b>{effMax}</b> characters allowed.</div>
        )}
      </div>
    );
  }

  // Numeric bounds
  if (['number', 'currency'].includes(q.type)) {
    return (
      <div className="grid grid-cols-4 gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid gap-1">
          <Label className="text-xs">Currency symbol</Label>
          <Input value={(cfg.currency as string) || (q.type === 'currency' ? 'R' : '')} onChange={(e) => update({ currency: e.target.value })} disabled={disabled} />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Min</Label>
          <Input type="number" value={(cfg.min as number) ?? ''} onChange={(e) => update({ min: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Max</Label>
          <Input type="number" value={(cfg.max as number) ?? ''} onChange={(e) => update({ max: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Decimals</Label>
          <Input type="number" value={(cfg.decimals as number) ?? (q.type === 'currency' ? 2 : 0)} onChange={(e) => update({ decimals: Number(e.target.value) })} disabled={disabled} className="font-tabular" />
        </div>
      </div>
    );
  }

  // Info block content
  if (q.type === 'info_block') {
    return (
      <div className="grid gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <Label className="text-xs">Info content (shown as a callout)</Label>
        <Textarea value={(cfg.body as string) || ''} onChange={(e) => update({ body: e.target.value })} disabled={disabled} rows={3} />
      </div>
    );
  }

  // Photo upload — single/multiple, max count, size limit, compression
  if (q.type === 'photo_ref') {
    const multiple = cfg.multiple !== false; // default: allow multiple
    return (
      <div className="grid gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <Label className="text-xs">How many photos</Label>
            <select
              className="rounded border border-border/60 bg-white px-2 py-1.5 text-sm font-body"
              value={multiple ? 'multiple' : 'single'}
              onChange={(e) => update({ multiple: e.target.value === 'multiple' })}
              disabled={disabled}
              data-testid={`cfg-photo-multiple-${testIdx}`}
            >
              <option value="single">Single photo only</option>
              <option value="multiple">Multiple photos</option>
            </select>
          </div>
          {multiple && (
            <div className="grid gap-1">
              <Label className="text-xs">Max photos</Label>
              <Input type="number" min={1} value={(cfg.max_count as number) ?? 5} onChange={(e) => update({ max_count: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-photo-max-${testIdx}`} />
            </div>
          )}
          <div className="grid gap-1">
            <Label className="text-xs">Max size per photo (MB)</Label>
            <Input type="number" min={1} step={0.5} value={(cfg.max_mb as number) ?? 10} onChange={(e) => update({ max_mb: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-photo-mb-${testIdx}`} />
          </div>
          <label className="flex items-center gap-2 text-xs text-raptor-navy mt-5">
            <input type="checkbox" checked={cfg.compress !== false} onChange={(e) => update({ compress: e.target.checked })} disabled={disabled} data-testid={`cfg-photo-compress-${testIdx}`} />
            Auto-compress photos over the size limit
          </label>
          <div className="grid gap-1">
            <Label className="text-xs">Default camera</Label>
            <select
              className="rounded border border-border/60 bg-white px-2 py-1.5 text-sm font-body"
              value={(cfg.camera_facing as string) ?? 'any'}
              onChange={(e) => update({ camera_facing: e.target.value })}
              disabled={disabled}
              data-testid={`cfg-photo-facing-${testIdx}`}
            >
              <option value="any">Either (ask device)</option>
              <option value="back">Rear camera</option>
              <option value="front">Front / selfie</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-raptor-navy mt-5">
            <input type="checkbox" checked={cfg.allow_switch !== false} onChange={(e) => update({ allow_switch: e.target.checked })} disabled={disabled} data-testid={`cfg-photo-switch-${testIdx}`} />
            Allow switching front/back while capturing
          </label>
        </div>
      </div>
    );
  }

  // Video — record live, duration + size limits, camera default
  if (q.type === 'video_ref') {
    return (
      <div className="grid grid-cols-2 gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid gap-1">
          <Label className="text-xs">Max record length (seconds)</Label>
          <Input type="number" min={5} max={300} value={(cfg.max_duration_sec as number) ?? 30} onChange={(e) => update({ max_duration_sec: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-video-dur-${testIdx}`} />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Max file size (MB)</Label>
          <Input type="number" min={1} max={200} step={1} value={(cfg.max_mb as number) ?? 50} onChange={(e) => update({ max_mb: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-video-mb-${testIdx}`} />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Default camera</Label>
          <select
            className="rounded border border-border/60 bg-white px-2 py-1.5 text-sm font-body"
            value={(cfg.camera_facing as string) ?? 'any'}
            onChange={(e) => update({ camera_facing: e.target.value })}
            disabled={disabled}
            data-testid={`cfg-video-facing-${testIdx}`}
          >
            <option value="any">Either (ask device)</option>
            <option value="back">Rear camera</option>
            <option value="front">Front / selfie</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-raptor-navy mt-5">
          <input type="checkbox" checked={cfg.allow_switch !== false} onChange={(e) => update({ allow_switch: e.target.checked })} disabled={disabled} data-testid={`cfg-video-switch-${testIdx}`} />
          Allow switching front/back
        </label>
        <div className="col-span-2 text-[11px] text-raptor-navy/60">Surveyor records live on their device — no file uploads. Recording stops automatically at the length limit; clips over the size limit are rejected. Stored in the tenant&rsquo;s OneDrive when connected.</div>
      </div>
    );
  }

  // Document upload — max count, size limit
  if (q.type === 'document_ref') {
    return (
      <div className="grid grid-cols-2 gap-2 rounded-md border border-border/40 p-2 bg-raptor-neutral/20">
        <div className="grid gap-1">
          <Label className="text-xs">Max documents</Label>
          <Input type="number" min={1} value={(cfg.max_count as number) ?? 10} onChange={(e) => update({ max_count: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-doc-max-${testIdx}`} />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Max size per file (MB)</Label>
          <Input type="number" min={1} step={0.5} value={(cfg.max_mb as number) ?? 2} onChange={(e) => update({ max_mb: e.target.value === '' ? null : Number(e.target.value) })} disabled={disabled} className="font-tabular" data-testid={`cfg-doc-mb-${testIdx}`} />
        </div>
        <div className="col-span-2 text-[11px] text-raptor-navy/60">Surveyor uploads PDF / JPG / PNG files. Images over the limit are auto-compressed; other files over the limit are rejected.</div>
      </div>
    );
  }

  return null;
};


export default TemplateEditorPage;


// -------------------------------------------------------------- Template settings dialog
function TemplateSettingsDialog({ tpl, computedScored, isDraft, geoMode, geoThresholdM, onSaveGeo, onClose, onSaved }: {
  tpl: Template;
  computedScored: boolean;
  isDraft: boolean;
  geoMode: 'strict' | 'capture_only';
  geoThresholdM: number;
  onSaveGeo: (mode: 'strict' | 'capture_only', thresholdM: number) => Promise<void>;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.ReactElement {
  const [name, setName] = useState(tpl.name);
  const [description, setDescription] = useState(tpl.description || '');
  const [active, setActive] = useState<boolean>(tpl.active ?? true);
  const [scoringEnabled, setScoringEnabled] = useState<boolean>(!!tpl.scoring_enabled);
  const [geoModeLocal, setGeoModeLocal] = useState<'strict' | 'capture_only'>(geoMode);
  const [geoThresholdLocal, setGeoThresholdLocal] = useState<number>(geoThresholdM);
  const [ratingMatrixId, setRatingMatrixId] = useState<string>(
    (tpl as unknown as { rating_matrix_id?: string }).rating_matrix_id ?? '',
  );
  const [matrixOptions, setMatrixOptions] = useState<Array<{ id: string; name: string; factors_count: number; is_published: boolean }>>([]);
  const [busy, setBusy] = useState(false);
  const initialMatrixId = (tpl as unknown as { rating_matrix_id?: string }).rating_matrix_id ?? '';

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<Array<{ id: string; name: string; factors_count: number; is_published: boolean }>>(
          '/risk/matrices',
        );
        setMatrixOptions(r.data);
      } catch (err) {
        // Non-fatal — dropdown just stays empty. Log for diagnostics.
        console.warn('risk-matrices load failed', err);
      }
    })();
  }, []);

  const save = async (): Promise<void> => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(), description, active, scoring_enabled: scoringEnabled,
      };
      await api.patch(`/templates/${tpl.id}`, payload);
      // Persist matrix link separately (dedicated endpoint enforces cross-insurer safety)
      if ((ratingMatrixId || '') !== (initialMatrixId || '')) {
        await api.put(`/risk/matrices/link/template/${tpl.id}`, {
          matrix_id: ratingMatrixId || null,
        });
      }
      // Persist the per-template geo policy (version schema). No-op on published versions.
      if (isDraft) await onSaveGeo(geoModeLocal, geoThresholdLocal);
      toast.success('Template updated');
      await onSaved();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string; error?: string } } };
      toast.error(e?.response?.data?.detail ?? e?.response?.data?.error ?? 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="template-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings size={18} className="text-raptor-blue" /> Template settings</DialogTitle>
          <DialogDescription>
            Rename the template, change its status, set the Score Rated flag or the on-site location policy.
            Question content is edited from the main editor.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" />
          </div>
          <div className="grid gap-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="settings-desc-input" />
          </div>
          <div className="grid gap-2 rounded-md border border-border/60 bg-raptor-neutral/20 p-3">
            <Label className="flex items-center gap-1">
              Rating matrix
              {scoringEnabled && !ratingMatrixId && (
                <Badge variant="warning" className="text-[10px] ml-1">Recommended</Badge>
              )}
            </Label>
            <select
              className="h-9 rounded-md border border-border/60 bg-white px-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
              value={ratingMatrixId}
              onChange={(e) => setRatingMatrixId(e.target.value)}
              data-testid="settings-matrix-select"
            >
              <option value="">— None (template will not be scored) —</option>
              {matrixOptions.map((m) => (
                <option key={m.id} value={m.id} disabled={!m.is_published}>
                  {m.name} ({m.factors_count} factor{m.factors_count === 1 ? '' : 's'})
                  {!m.is_published ? ' — draft' : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-raptor-navy/60">
              Determines which named risk matrix drives the scoring engine for surveys built from this template. Assessments already computed keep their frozen matrix version.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm font-body cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="settings-active-toggle" />
            <div>
              <div className="font-semibold text-raptor-navy">Active</div>
              <div className="text-xs text-raptor-navy/60">Inactive templates cannot be used to issue new instructions.</div>
            </div>
          </label>
          <div className="grid gap-2 rounded-md border border-border/60 bg-raptor-neutral/20 p-3" data-testid="geo-policy-card">
            <div className="flex items-center gap-2 text-raptor-navy font-semibold text-sm"><MapPin size={16} /> On-site location</div>
            <p className="text-[11px] text-raptor-navy/60 -mt-1">
              How the surveyor&apos;s location is captured for every survey built from this template. Location is always recorded and the detected address confirmed.
              {!isDraft && ' Editable only on a draft version.'}
            </p>
            <label className={`flex items-start gap-2 rounded-md border p-2.5 ${isDraft ? 'cursor-pointer' : 'opacity-70'} ${geoModeLocal === 'strict' ? 'border-raptor-blue bg-raptor-blue/5' : 'border-border/60'}`}>
              <input type="radio" name="geo-mode" className="mt-1" disabled={!isDraft} checked={geoModeLocal === 'strict'} onChange={() => setGeoModeLocal('strict')} data-testid="geo-mode-strict" />
              <div>
                <div className="font-semibold text-sm text-raptor-navy">Strict — check distance</div>
                <div className="text-xs text-raptor-navy/60">Capture + confirm address, then compare to the site&apos;s registered coordinates. If outside the allowed distance it&apos;s flagged for the underwriter — the survey is never blocked. Requires the site to be geocoded before issuing.</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 rounded-md border p-2.5 ${isDraft ? 'cursor-pointer' : 'opacity-70'} ${geoModeLocal === 'capture_only' ? 'border-raptor-blue bg-raptor-blue/5' : 'border-border/60'}`}>
              <input type="radio" name="geo-mode" className="mt-1" disabled={!isDraft} checked={geoModeLocal === 'capture_only'} onChange={() => setGeoModeLocal('capture_only')} data-testid="geo-mode-capture" />
              <div>
                <div className="font-semibold text-sm text-raptor-navy">Capture-only — no distance check</div>
                <div className="text-xs text-raptor-navy/60">Record the surveyor&apos;s location + confirmed address, but never check distance or flag anything.</div>
              </div>
            </label>
            {geoModeLocal === 'strict' && (
              <div className="grid gap-1 max-w-xs">
                <Label className="text-xs">Allowed distance from site (metres)</Label>
                <Input type="number" min={10} disabled={!isDraft} value={geoThresholdLocal}
                  onChange={(e) => setGeoThresholdLocal(Math.max(10, parseInt(e.target.value || '0', 10) || 0))}
                  data-testid="geo-threshold-input" />
                <div className="text-[11px] text-raptor-navy/50">Surveyors beyond this distance are flagged (with an optional reason) but can still continue.</div>
              </div>
            )}
          </div>
          <div className="rounded-md border border-border/60 bg-raptor-neutral/30 p-3">
            <label className="flex items-center gap-2 text-sm font-body cursor-pointer">
              <input type="checkbox" checked={scoringEnabled} onChange={(e) => setScoringEnabled(e.target.checked)} data-testid="settings-scoring-toggle" />
              <div>
                <div className="font-semibold text-raptor-navy flex items-center gap-1">
                  Score Rated
                  {scoringEnabled
                    ? <Badge variant="blue" className="ml-1">Yes</Badge>
                    : <Badge variant="muted" className="ml-1">No</Badge>}
                </div>
                <div className="text-xs text-raptor-navy/60">
                  Manually flag this template as feeding the Risk Ratings engine. This overrides the
                  auto-detected value (based on risk-weighted Yes/No questions).
                </div>
              </div>
            </label>
            <div className="mt-2 text-[11px] text-raptor-navy/60 flex items-center gap-1">
              <span>Auto-detected from schema:</span>
              {computedScored
                ? <Badge variant="blue" className="gap-1"><ShieldCheck size={10} /> Yes</Badge>
                : <Badge variant="muted" className="gap-1"><MinusCircle size={10} /> No</Badge>}
              {computedScored !== scoringEnabled && (
                <span className="ml-2 italic text-amber-600">Your setting overrides the auto-detected value.</span>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="gradient" onClick={() => void save()} disabled={busy} data-testid="settings-save-btn">
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
